import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { AxiosResponse } from 'axios';

import ApiService from '../../services/apiService';
import { Filters } from '../../interfaces/filters';
import { Shipper, ShippersResponse } from '../../interfaces/services/shippers';
import { NetworkKpi } from '../../interfaces/services/kpis';
import { ApiStatus } from '../../utils/enums';
import { getTTCBaseUrl } from '../../utils/environmentUtils';

interface ShipperState {
    shippersApiStatus: ApiStatus;
    shippers: Shipper[];
    shippersCount: number;
    KPIApiStatus: ApiStatus;
    KPIs: NetworkKpi;
}

export const fetchShippers = createAsyncThunk(
    'shippers/fetchShippers',
    async ({
        filter_criteria,
    }: {
        filter_criteria?: Filters;
    }) => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/pickup-parties`,
                body: {
                    filter_criteria,
                },
            }) as AxiosResponse<ShippersResponse>;

            return data;
        } catch (error) {
            console.error('Unable to load shippers', error);
            toast.error('Unable to load shippers');
            throw error;
        }
    }
);

export const fetchShipperKpis = createAsyncThunk(
    'shippers/fetchShipperKpis',
    async ({
        filter_criteria,
    }: {
        filter_criteria?: Filters;
    }) => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/kpi`,
                body: {
                    filter_criteria,
                },
            }) as AxiosResponse<NetworkKpi>;

            return data;
        } catch (error) {
            console.error('Unable to load shipper KPIs', error);
            toast.error('Unable to load shipper KPIs');
            throw error;
        }
    }
);

const initialState: ShipperState = {
    shippersApiStatus: ApiStatus.Idle,
    shippers: [],
    shippersCount: 0,
    KPIApiStatus: ApiStatus.Idle,
    KPIs: {
        carrier_network_grade: null,
        lane_count: null,
        freight_order_count: null,
        total_revenue_usd_amount: null,
        linehaul_revenue_usd_amount: null,
        fuel_surcharge_usd_amount: null,
        accessorial_revenue_usd_amount: null,
        travel_distance_miles_amount: null,
        billable_distance_miles_amount: null,
        loaded_distance_miles_amount: null,
        empty_distance_miles_amount: null,
        pta_to_last_stop_departure_hours_duration: null,
        dispatch_to_last_stop_departure_hours_duration: null,
        first_stop_to_last_stop_hours_duration: null,
        first_stop_departure_to_final_stop_arrival_hours_duration: null,
        standard_transit_hours_duration: null,
        netwise_dwell_hours_duration: null,
        netwise_in_transit_delay_hours_duration: null,
        netwise_delay_hours_duration: null,
        load_hours_duration: null,
        unload_hours_duration: null,
        empty_transit_hours_duration: null,
        pickup_party_count: null,
        commodity_count: null,
        average_dispatch_to_last_stop_departure_hours_duration: null,
        average_first_stop_to_last_stop_hours_duration: null,
        average_first_stop_departure_to_final_stop_arrival_hours_duration: null,
        average_standard_transit_hours_duration: null,
        average_netwise_in_transit_delay_hours_duration: null,
        average_load_hours_duration: null,
        average_unload_hours_duration: null,
        average_empty_transit_hours_duration: null,
        average_network_order_yield_usd: null,
    },
};

const shippperSlice = createSlice({
    name: 'shippers',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder.addCase(fetchShippers.pending, (state) => {
            state.shippersApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchShippers.fulfilled, (state, action) => {
            state.shippersApiStatus = ApiStatus.Success;
            state.shippers = action.payload.pickup_parties;
            state.shippersCount = action.payload.pickup_party_count;
        });
        builder.addCase(fetchShippers.rejected, (state) => {
            state.shippersApiStatus = ApiStatus.Failure;
            state.shippers = [];
            state.shippersCount = 0;
        });
        builder.addCase(fetchShipperKpis.pending, (state) => {
            state.KPIApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchShipperKpis.fulfilled, (state, action) => {
            state.KPIApiStatus = ApiStatus.Success;
            state.KPIs = action.payload;
        });
        builder.addCase(fetchShipperKpis.rejected, (state) => {
            state.KPIApiStatus = ApiStatus.Failure;
            state.KPIs = initialState.KPIs;
        });
    },
});

export default shippperSlice.reducer;
